import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Flame, Sparkles, Trophy, Loader2, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const TIER_STYLES = {
  newbie:     { label: 'Just Getting Started', color: 'text-[#A0B4CC]', bg: 'from-[#1E2A45] to-[#0F1B30]', icon: TrendingUp, iconColor: 'text-[#A0B4CC]', emoji: '🌱' },
  rising:     { label: 'Rising Earner',         color: 'text-[#00E5FF]', bg: 'from-[#00E5FF]/15 to-[#041A3B]', icon: Flame, iconColor: 'text-[#00E5FF]', emoji: '🔥' },
  consistent: { label: 'Consistent Earner',     color: 'text-[#7C4DFF]', bg: 'from-[#7C4DFF]/20 to-[#041A3B]', icon: Flame, iconColor: 'text-[#7C4DFF]', emoji: '⚡' },
  star:       { label: 'Star Earner',           color: 'text-[#FFEA00]', bg: 'from-[#FFEA00]/15 to-[#041A3B]', icon: Sparkles, iconColor: 'text-[#FFEA00]', emoji: '⭐' },
  legend:     { label: 'AGA Legend',            color: 'text-[#FF6B35]', bg: 'from-[#FF6B35]/20 via-[#FFEA00]/10 to-[#041A3B]', icon: Trophy, iconColor: 'text-[#FF6B35]', emoji: '👑' },
};

const NEXT_TIER_THRESHOLDS = { newbie: 1, rising: 3, consistent: 6, star: 12, legend: null };

export function EarningStreakCard() {
  const { api } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/referrals/streak')
      .then(({ data }) => setStreak(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) {
    return (
      <Card className="bg-[#041A3B] border-white/10 mb-4" data-testid="earning-streak-card">
        <CardContent className="p-5 flex items-center gap-3 text-[#A0B4CC] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your earning streak...
        </CardContent>
      </Card>
    );
  }
  if (!streak) return null;

  const t = TIER_STYLES[streak.tier] || TIER_STYLES.newbie;
  const Icon = t.icon;
  const threshold = NEXT_TIER_THRESHOLDS[streak.tier];
  const progressLabel = threshold
    ? `${threshold - streak.current_streak} more month${threshold - streak.current_streak === 1 ? '' : 's'} to next tier`
    : 'Maxed out — keep it going!';

  const history = (streak.history || []).slice().reverse(); // oldest → newest for sparkline

  return (
    <Card className={`keep-dark bg-gradient-to-br ${t.bg} border-white/10 mb-4 overflow-hidden relative`} data-testid="earning-streak-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-4xl leading-none" aria-hidden>{t.emoji}</div>
            <div>
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${t.iconColor}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${t.color}`} data-testid="streak-tier-label">{t.label}</span>
              </div>
              <p className="text-white text-2xl font-bold mt-1 font-outfit" data-testid="streak-current-value">
                {streak.current_streak} {streak.current_streak === 1 ? 'month' : 'months'} streak
              </p>
              <p className="text-[#A0B4CC] text-xs mt-0.5">
                Longest: {streak.longest_streak} mo · This month: ₱{Number(streak.current_month_earnings || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>

          {/* Mini 12-month sparkline */}
          <div className="flex items-end gap-0.5 h-12" data-testid="streak-sparkline">
            {history.map((h) => {
              const max = Math.max(...history.map((x) => x.total || 0), 1);
              const heightPct = (h.total / max) * 100 || 4;
              const hasEarnings = (h.count || 0) > 0;
              return (
                <div key={h.month} className="flex flex-col items-center gap-1" title={`${h.month} · ₱${(h.total || 0).toFixed(2)} (${h.count || 0} commissions)`}>
                  <div
                    className={`w-2 rounded-sm ${hasEarnings ? 'bg-[#00E676]' : 'bg-white/10'}`}
                    style={{ height: `${Math.max(heightPct, 6)}%`, minHeight: '4px' }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress to next tier */}
        <div className="mt-4 pt-3 border-t border-white/10 text-xs text-[#A0B4CC]" data-testid="streak-next-tier">
          {streak.tier === 'newbie' ? (
            <>Refer at least one paying member this month to start your streak.</>
          ) : (
            <>{progressLabel}</>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
